import type { DateTime } from "luxon";
import type { FC } from "react";
import { CommonDashboardBox } from "../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../components/Dashboard/CommonDashboardHeader";
import InHouseBarChart from "../../components/UIToolkit/Graphs/BarChart/InHouseBarChart";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { HOSPITAL_DEMO } from "../../theme/tokens";

// Functional / Categorical: census/admission/discharge chart series. Must stay stable across themes.
const CENSUS_CHART_COLORS = {
	CENSUS: "var(--inhouse-primary-light)80",
	ADMISSION: "#B5EBCF",
	DISCHARGE: "#F57E5D",
} as const;

// Hospital demo palette: magenta census line, teal admission/discharge bars.
const DEMO_CENSUS_CHART_COLORS = {
	CENSUS: HOSPITAL_DEMO.CENSUS_LINE,
	ADMISSION: HOSPITAL_DEMO.CENSUS_ADMISSION,
	DISCHARGE: HOSPITAL_DEMO.CENSUS_DISCHARGE,
} as const;

type Props = {
	date: DateTime;
	lastDateInWeeksData?: DateTime;
};

type DailyValues = {
	census: number;
	admissions: number;
	discharges: number;
	date: DateTime;
};

export const CensusOverTime: FC<Props> = ({ date, lastDateInWeeksData }) => {
	const { hospitalData, getWeekIndexByDate, getDayIndexByDate, isHospitalView } = useHospitalData();
	// Only hospital demos (Hospital type + inhouse2).
	const chartColors = isHospitalView ? DEMO_CENSUS_CHART_COLORS : CENSUS_CHART_COLORS;
	const buildDailyValues = (date: DateTime): DailyValues[] => {
		/**
		 * We take 1 week before the date and 1 week after the date
		 */
		const data: DailyValues[] = [];
		const censusValues: number[] = [];
		const isLastWeekPartial = lastDateInWeeksData ? lastDateInWeeksData.diff(date, "days").as("days") <= 7 : false;
		const totalDays = lastDateInWeeksData ? (isLastWeekPartial ? lastDateInWeeksData.diff(date, "days").as("days") + 1 : 15) : 15;
		const startDate = isLastWeekPartial ? date : date.minus({ days: 7 });
		for (let index = 0; index < totalDays; index++) {
			const currentDate = startDate.plus({ days: index });
			const census = Math.max(
				...(hospitalData.weeks[getWeekIndexByDate(currentDate)]?.weeklyShifts.flatMap(
					(weeklyData) =>
						weeklyData.days[getDayIndexByDate(currentDate)]?.census_override?.override_value ??
						weeklyData.days[getDayIndexByDate(currentDate)]?.census_high ??
						0,
				) ?? [0]),
			);
			censusValues.push(census);
		}
		for (let index = 0; index < totalDays; index++) {
			const currentDate = startDate.plus({ day: index });

			// Hospital demo: read the same admissions and discharges the Shift Insights page shows,
			// so the two pages agree. Shift Insights shows a range like "0-1" per shift, and we take
			// the top of it. Both shifts are added up, because this chart has one bar per day.
			if (isHospitalView) {
				const days = hospitalData.weeks[getWeekIndexByDate(currentDate)]?.weeklyShifts.map((weeklyData) => weeklyData.days[getDayIndexByDate(currentDate)]);
				data.push({
					census: censusValues[index],
					admissions: days?.reduce((sum, day) => sum + (day?.shiftEvents?.numberAdmissionsHigh ?? 0), 0) ?? 0,
					discharges: days?.reduce((sum, day) => sum + (day?.shiftEvents?.numberDischargesHigh ?? 0), 0) ?? 0,
					date: currentDate,
				});
				continue;
			}

			// Real departments have no stored admissions, so they keep the estimate from the census
			// change: a rise means people arrived, a fall means people left.
			const today = censusValues[index];
			const next = censusValues[index + 1];
			const delta = next - today;
			let admissions = 0;
			let discharges = 0;
			const eps = currentDate.day % 3; // Math.floor(Math.random() * 3); // 0–6 random noise
			if (delta >= 0) {
				admissions = delta + eps;
				discharges = +eps;
			} else {
				admissions = +eps;
				discharges = -delta + +eps;
			}
			data.push({
				census: censusValues[index],
				admissions,
				discharges: discharges >= 0 ? discharges : 0,
				date: currentDate,
			});
		}
		return data;
	};

	const dailyValues = buildDailyValues(date);
	const dailyValuesWithLabels = dailyValues.map((d, i) => ({
		...d,
	}));

	return (
		<CommonDashboardBox
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				justifyContent: "center",
				gap: 1,
				flex: 1,
			}}
		>
			<CommonDashboardHeader label={"Census Over Time"} />
			<InHouseBarChart
				width={"100%"}
				rightAxisLineData={{
					name: "Census",
					color: chartColors.CENSUS,
					graphCurve: "linear",
					data: dailyValuesWithLabels.map((value) => ({
						date: value.date,
						value: value.census,
					})),
				}}
				stackedGroups={[
					[
						{
							name: "Admission",
							color: chartColors.ADMISSION,
							data: dailyValuesWithLabels.map((value) => ({
								dateOrLabel: value.date,
								value: value.admissions,
							})),
						},
					],
					[
						{
							name: "Discharge",
							color: chartColors.DISCHARGE,
							data: dailyValuesWithLabels.map((value) => ({
								dateOrLabel: value.date,
								value: value.discharges,
							})),
						},
					],
				]}
			/>
		</CommonDashboardBox>
	);
};
